"use client";

import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthProvider";
import halfMarathonTrainingPlan from "../plans/half-marathon/data/halfMarathonTrainingPlan.json";

const DAYS = [
    { key: "monday", label: "Monday", shortLabel: "Mon" },
    { key: "tuesday", label: "Tuesday", shortLabel: "Tue" },
    { key: "wednesday", label: "Wednesday", shortLabel: "Wed" },
    { key: "thursday", label: "Thursday", shortLabel: "Thu" },
    { key: "friday", label: "Friday", shortLabel: "Fri" },
    { key: "saturday", label: "Saturday", shortLabel: "Sat" },
    { key: "sunday", label: "Sunday", shortLabel: "Sun" },
];

const PROGRAM_ID = "half-marathon";
const PROGRAM_TITLE = "Half Marathon Training Plan";

const WORKOUT_STYLES = {
    "optional-cross-training": {
        background: "#ffb900",
        border: "1px solid rgba(20, 184, 166, 0.24)",
    },
    "easy-run": {
        background: "#1eb778",
        border: "1px solid rgba(34, 197, 94, 0.24)",
    },
    "optional-easy-run": {
        background: "#006bc1",
        border: "1px solid rgba(132, 204, 22, 0.24)",
    },
    "long-run": {
        background: "#7956ff",
        border: "1px solid rgba(139, 92, 246, 0.24)",
    },
    race: {
        background: "#ff5f00",
        border: "1px solid rgba(249, 115, 22, 0.26)",
    },
    rest: {
        background: "#cbd5e1",
        border: "1px solid rgba(148, 163, 184, 0.22)",
    },
};

function isDistanceWorkout(value) {
    return Boolean(value) && typeof value === "object";
}

function isRestWorkout(value) {
    return value == null || value === "Rest";
}

function isRaceDayWorkout(value) {
    return value === "Race Day";
}

function isTimeBasedWorkout(value) {
    return typeof value === "string" && value.toLowerCase().includes("min");
}

function formatDistance(value) {
    if (!isDistanceWorkout(value)) {
        return "";
    }

    const km = typeof value.km === "number" ? `${value.km} km` : "";
    const miles = typeof value.miles === "number" ? `${value.miles} mi` : "";

    if (km && miles) {
        return `${km} (${miles})`;
    }

    return km || miles;
}

function getWorkoutDefinition(day, value, total, weekNumber) {
    if (isRestWorkout(value)) {
        return {
            weekNumber,
            dayKey: day.key,
            dayLabel: day.label,
            shortLabel: day.shortLabel,
            title: "Rest Day",
            type: "rest",

            detailValue: "Rest",
            description:
                "Take the day off. Use the extra recovery to sleep well, hydrate, and get ready for your next workout.",
            canMarkComplete: false,
            progressKey: `week-${weekNumber}-${day.key}`,
            totalLabel: formatDistance(total),
        };
    }

    if (isRaceDayWorkout(value)) {
        return {
            weekNumber,
            dayKey: day.key,
            dayLabel: day.label,
            shortLabel: day.shortLabel,
            title: "Race Day",
            type: "race",
            summary: "Race Day",
            detailValue: "Half Marathon Race",
            description:
                "Race your half marathon today. Start controlled, settle into a sustainable rhythm, and save your strongest push for the final part of the race.",
            canMarkComplete: true,
            progressKey: `week-${weekNumber}-${day.key}`,
            totalLabel: formatDistance(total),
        };
    }

    if (isTimeBasedWorkout(value)) {
        return {
            weekNumber,
            dayKey: day.key,
            dayLabel: day.label,
            shortLabel: day.shortLabel,
            title: "Optional XT",
            type: "optional-cross-training",
            summary: value,
            detailValue: value,
            description: `Use ${value} for optional cross-training at an easy, controlled effort. Walking, cycling, swimming, elliptical, mobility work, or light strength are all good options if they help you feel stronger and fresher, not more fatigued.`,
            canMarkComplete: true,
            progressKey: `week-${weekNumber}-${day.key}`,
            totalLabel: formatDistance(total),
        };
    }

    const distanceLabel = formatDistance(value);

    if (day.key === "saturday") {
        return {
            weekNumber,
            dayKey: day.key,
            dayLabel: day.label,
            shortLabel: day.shortLabel,
            title: "Opt. Run",
            type: "optional-easy-run",
            summary: distanceLabel,
            detailValue: distanceLabel,
            description: `This is an optional extra easy run. Cover ${distanceLabel} at a relaxed, conversational effort and skip it if your body needs more recovery this week.`,
            canMarkComplete: true,
            progressKey: `week-${weekNumber}-${day.key}`,
            totalLabel: formatDistance(total),
        };
    }

    if (day.key === "sunday") {
        return {
            weekNumber,
            dayKey: day.key,
            dayLabel: day.label,
            shortLabel: day.shortLabel,
            title: "Long Run",
            type: "long-run",
            summary: distanceLabel,
            detailValue: distanceLabel,
            description: `Complete your long run at an easy, steady effort. Cover ${distanceLabel} and focus on relaxed form, patient pacing, and building endurance for race day. It's ok to walk if needed.`,
            canMarkComplete: true,
            progressKey: `week-${weekNumber}-${day.key}`,
            totalLabel: formatDistance(total),
        };
    }

    return {
        weekNumber,
        dayKey: day.key,
        dayLabel: day.label,
        shortLabel: day.shortLabel,
        title: "Easy Run",
        type: "easy-run",
        summary: distanceLabel,
        detailValue: distanceLabel,
        description: `Run ${distanceLabel} at an easy, conversational effort. Stay relaxed, keep the pace controlled, and finish feeling like you could have kept going a little longer. It's ok to walk if you need to.`,
        canMarkComplete: true,
        progressKey: `week-${weekNumber}-${day.key}`,
        totalLabel: formatDistance(total),
    };
}

function buildPlanWeeks() {
    return halfMarathonTrainingPlan.plan.map((weekEntry) => ({
        weekNumber: weekEntry.week,
        total: weekEntry.total,
        workouts: DAYS.map((day) =>
            getWorkoutDefinition(day, weekEntry[day.key], weekEntry.total, weekEntry.week)
        ),
    }));
}

export default function HalfMarathonPlanViewer() {
    const { user } = useAuth();
    const [completedProgressKeys, setCompletedProgressKeys] = useState(() => new Set());
    const [completionLoadError, setCompletionLoadError] = useState("");
    const [completionActionError, setCompletionActionError] = useState("");
    const [savingProgressKey, setSavingProgressKey] = useState("");
    const [selectedWorkout, setSelectedWorkout] = useState(null);
    const planWeeks = buildPlanWeeks();

    useEffect(() => {
        if (!user?.uid) {
            setCompletedProgressKeys(new Set());
            setCompletionLoadError("");
            return undefined;
        }

        const completionsRef = collection(
            db,
            "user_activities",
            user.uid,
            "programs",
            PROGRAM_ID,
            "completions"
        );

        return onSnapshot(
            completionsRef,
            (snapshot) => {
                setCompletedProgressKeys(new Set(snapshot.docs.map((docSnap) => docSnap.id)));
                setCompletionLoadError("");
            },
            (error) => {
                console.error("Unable to load half marathon completions", error);
                setCompletedProgressKeys(new Set());
                setCompletionLoadError("Unable to load your saved half marathon progress right now.");
            }
        );
    }, [user?.uid]);

    useEffect(() => {
        setCompletionActionError("");
    }, [selectedWorkout?.progressKey]);

    const trackableWorkouts = planWeeks.flatMap((week) =>
        week.workouts.filter((workout) => workout.canMarkComplete)
    );
    const completedSessionCount = trackableWorkouts.filter((workout) =>
        completedProgressKeys.has(workout.progressKey)
    ).length;
    const percentComplete = trackableWorkouts.length
        ? Math.round((completedSessionCount / trackableWorkouts.length) * 100)
        : 0;

    const handleCloseWorkoutModal = () => {
        setCompletionActionError("");
        setSelectedWorkout(null);
    };

    const handleToggleCompletion = async (workout) => {
        if (!user?.uid || !workout?.canMarkComplete || !workout.progressKey) {
            if (!user?.uid) {
                setCompletionActionError("Please sign in to save your progress.");
            }
            return;
        }

        const progressKey = workout.progressKey;
        const wasCompleted = completedProgressKeys.has(progressKey);
        const previousCompletedKeys = new Set(completedProgressKeys);
        const nextCompletedKeys = new Set(completedProgressKeys);

        setSavingProgressKey(progressKey);
        setCompletionActionError("");

        if (wasCompleted) {
            nextCompletedKeys.delete(progressKey);
        } else {
            nextCompletedKeys.add(progressKey);
        }

        setCompletedProgressKeys(nextCompletedKeys);

        try {
            const programRef = doc(db, "user_activities", user.uid, "programs", PROGRAM_ID);
            const completionRef = doc(
                db,
                "user_activities",
                user.uid,
                "programs",
                PROGRAM_ID,
                "completions",
                progressKey
            );

            await setDoc(
                programRef,
                {
                    programId: PROGRAM_ID,
                    title: PROGRAM_TITLE,
                    distanceLabel: "21K",
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );

            if (wasCompleted) {
                await deleteDoc(completionRef);
            } else {
                await setDoc(completionRef, {
                    programId: PROGRAM_ID,
                    title: PROGRAM_TITLE,
                    progressKey,
                    weekNumber: workout.weekNumber,
                    dayKey: workout.dayKey,
                    dayLabel: workout.dayLabel,
                    workoutTitle: workout.title,
                    workoutSummary: workout.detailValue,
                    completedAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });
            }
        } catch (error) {
            console.error("Unable to update half marathon completion", error);
            setCompletedProgressKeys(previousCompletedKeys);
            setCompletionActionError("Unable to save this workout right now. Please try again.");
        } finally {
            setSavingProgressKey("");
        }
    };

    return (
        <div className="p-2">
            <h2 className="text-2xl font-bold mb-3">Your Training Plan</h2>
            <p className="mb-4">
                Click any day to see the instructions for that workout and mark completed sessions.
            </p>

            {!user && (
                <div
                    className="rounded-2xl px-4 py-3 mb-4 text-sm"
                    style={{
                        background: "rgba(255, 247, 237, 0.92)",
                        border: "1px solid rgba(249, 115, 22, 0.16)",
                        color: "#9a3412",
                    }}
                >
                    Sign in to save completed workouts to your progress history.
                </div>
            )}

            {completionLoadError && (
                <div
                    className="rounded-2xl px-4 py-3 mb-4 text-sm"
                    style={{
                        background: "rgba(254, 242, 242, 0.92)",
                        border: "1px solid rgba(248, 113, 113, 0.2)",
                        color: "#b91c1c",
                    }}
                >
                    {completionLoadError}
                </div>
            )}

            <div
                className="rounded-3xl p-4 mb-5"
                style={{
                    background: "linear-gradient(135deg, #f8fafc 0%, #eef4ff 100%)",
                    border: "1px solid rgba(99, 102, 241, 0.14)",
                }}
            >
                <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-sm font-semibold" style={{ color: "#1e293b" }}>
                        Training progress
                    </span>
                    <span className="text-sm font-semibold" style={{ color: "#334155" }}>
                        {completedSessionCount}/{trackableWorkouts.length} sessions completed
                    </span>
                </div>
                <div
                    className="w-full h-2 rounded-full overflow-hidden"
                    style={{ background: "rgba(148, 163, 184, 0.18)" }}
                >
                    <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                            width: `${percentComplete}%`,
                            background: percentComplete > 0
                                ? "linear-gradient(90deg, #16a34a 0%, #22c55e 100%)"
                                : "linear-gradient(90deg, #cbd5e1 0%, #e2e8f0 100%)",
                        }}
                    />
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse">
                    <thead>
                        <tr>
                            <th className="border border-gray-300 p-2">Week</th>
                            {DAYS.map((day) => (
                                <th key={day.key} className="border border-gray-300 p-2">
                                    {day.shortLabel}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {planWeeks.map((week) => (
                            <tr key={week.weekNumber} className="border border-gray-300">
                                <td className="border border-gray-300 p-2 font-semibold text-center align-top">
                                    <div>Week {week.weekNumber}</div>
                                </td>
                                {week.workouts.map((workout) => {
                                    const isCompleted = completedProgressKeys.has(workout.progressKey);
                                    const workoutStyle = WORKOUT_STYLES[workout.type];

                                    return (
                                        <td
                                            key={workout.progressKey}
                                            className="border border-gray-300 p-2 text-center align-top"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => setSelectedWorkout(workout)}
                                                className="w-full h-full block rounded-md p-2 text-white font-semibold"
                                                style={{
                                                    ...workoutStyle,
                                                    boxShadow: isCompleted
                                                        ? "0 0 0 2px rgba(34, 197, 94, 0.35) inset"
                                                        : "none",
                                                    opacity: workout.type === "rest" ? 0.92 : 1,
                                                }}
                                            >
                                                <span className="block">{workout.title}</span>
                                                <span className="block text-sm font-normal mt-1">
                                                    {workout.summary}
                                                </span>
                                                {isCompleted && workout.canMarkComplete && (
                                                    <span className="inline-block mt-2 text-[11px] font-bold bg-white/20 rounded-full px-2 py-0.5">
                                                        Completed
                                                    </span>
                                                )}
                                            </button>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selectedWorkout && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    onClick={handleCloseWorkoutModal}
                >
                    <div
                        className="bg-white rounded-md p-4 max-w-md w-full mx-2 relative max-h-[85vh] overflow-y-auto"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={handleCloseWorkoutModal}
                            className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 transition-colors"
                            aria-label="Close modal"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>

                        <h3 className="text-xl font-bold mb-2">{selectedWorkout.title}</h3>

                        <div className="space-y-2 mb-4">
                            <p className="mb-0">
                                <span className="font-semibold">Week:</span>{" "}
                                {selectedWorkout.weekNumber}
                            </p>
                            <p className="mb-0">
                                <span className="font-semibold">Day:</span>{" "}
                                {selectedWorkout.dayLabel}
                            </p>
                            <p className="mb-0">
                                <span className="font-semibold">Distance/Time:</span>{" "}
                                {selectedWorkout.detailValue}
                            </p>
                        </div>

                        <div className="mb-4">
                            <p className="font-semibold mb-1">Instructions</p>
                            <p className="mb-0 leading-7">{selectedWorkout.description}</p>
                        </div>

                        {selectedWorkout.canMarkComplete && user && (
                            <div className="mb-4">
                                <button
                                    type="button"
                                    onClick={() => handleToggleCompletion(selectedWorkout)}
                                    disabled={savingProgressKey === selectedWorkout.progressKey}
                                    className="inline-flex items-center justify-center rounded-xl px-3 py-2 text-left transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-70"
                                    style={{
                                        background: completedProgressKeys.has(selectedWorkout.progressKey)
                                            ? "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)"
                                            : "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                                        border: completedProgressKeys.has(selectedWorkout.progressKey)
                                            ? "1px solid rgba(22, 163, 74, 0.35)"
                                            : "1px solid rgba(34, 197, 94, 0.24)",
                                        color: completedProgressKeys.has(selectedWorkout.progressKey)
                                            ? "#ffffff"
                                            : "#166534",
                                        boxShadow: completedProgressKeys.has(selectedWorkout.progressKey)
                                            ? "0 10px 30px rgba(34, 197, 94, 0.2)"
                                            : "none",
                                    }}
                                >
                                    <span className="block text-sm font-bold">
                                        {savingProgressKey === selectedWorkout.progressKey
                                            ? "Saving progress..."
                                            : completedProgressKeys.has(selectedWorkout.progressKey)
                                                ? "Completed"
                                                : "Mark as completed"}
                                    </span>
                                </button>

                                {completedProgressKeys.has(selectedWorkout.progressKey)
                                    && savingProgressKey !== selectedWorkout.progressKey && (
                                        <p className="text-xs mt-2 mb-0" style={{ color: "#64748b" }}>
                                            Tap again if you want to mark this workout as incomplete.
                                        </p>
                                    )}
                            </div>
                        )}

                        {selectedWorkout.canMarkComplete && !user && (
                            <p className="text-sm mb-4" style={{ color: "#9a3412" }}>
                                Sign in to save completed workouts.
                            </p>
                        )}

                        {completionActionError && (
                            <p className="text-sm mb-0" style={{ color: "#b91c1c" }}>
                                {completionActionError}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
